import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import VideoPlayer from '@/pages/Courses/ClassDetail/Partials/VideoPlayer';
import { ChevronLeft, ChevronRight, Clock, Play, User, Video, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LEVELS, LEVEL_ORDER, LevelKey } from './liveSessionsConfig';

interface Professor {
    id: number;
    name: string;
    bio: string;
    avatar_url: string | null;
    order: number;
}

interface LanguageLiveSession {
    id: number;
    title: string;
    level: LevelKey;
    video_url: string;
    vimeo_url: string;
    duration_minutes: number;
    professor: Professor | null;
}

interface Props {
    language_live_sessions: LanguageLiveSession[];
}

const ALL_LEVELS = 'all';
const PAGE_SIZE = 24;

const LanguageLiveSessionsContent: React.FC<Props> = ({ language_live_sessions }) => {
    const { t } = useTranslation();
    const [levelFilter, setLevelFilter] = useState<LevelKey | typeof ALL_LEVELS>(ALL_LEVELS);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [activeSession, setActiveSession] = useState<LanguageLiveSession | null>(null);

    const filteredSessions = useMemo(() => {
        const term = search.trim().toLowerCase();
        return language_live_sessions.filter((session) => {
            const matchesLevel = levelFilter === ALL_LEVELS || session.level === levelFilter;
            const matchesSearch =
                term === '' ||
                session.title.toLowerCase().includes(term) ||
                (session.professor?.name.toLowerCase().includes(term) ?? false);
            return matchesLevel && matchesSearch;
        });
    }, [language_live_sessions, levelFilter, search]);

    // Reset to the first page whenever the filters narrow or widen the result set.
    useEffect(() => {
        setPage(1);
    }, [levelFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filteredSessions.length / PAGE_SIZE));
    const pagedSessions = useMemo(
        () => filteredSessions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredSessions, page]
    );

    const hasSessions = language_live_sessions.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Live Sessions Grabadas" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                    <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        🎥 {t('languageLiveSessions.title')}
                    </h1>
                    <p className="text-center text-gray-700 dark:text-neutral-300">
                        {t('languageLiveSessions.intro')}
                    </p>
                </div>

                {/* Levels legend */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                    <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                        🎓 {t('liveSessions.legendTitle')}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {LEVEL_ORDER.map((key) => {
                            const level = LEVELS[key];
                            const cefr = level.cefr.includes('.') ? t(level.cefr) : level.cefr;
                            return (
                                <div key={key} className="flex flex-col">
                                    <div
                                        className="rounded-t-lg px-4 py-3 text-white"
                                        style={{ backgroundColor: level.color }}
                                    >
                                        <div className="font-bold">{t(level.labelKey)}</div>
                                        <div className="text-xs opacity-90">{cefr}</div>
                                    </div>
                                    <p className="rounded-b-lg border border-t-0 border-gray-200 p-3 text-sm text-gray-600 dark:border-neutral-700 dark:text-neutral-400">
                                        {t(level.descKey)}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                            🎓 {t('liveSessions.filterLevel')}
                        </span>
                        <select
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value as LevelKey | typeof ALL_LEVELS)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                        >
                            <option value={ALL_LEVELS}>{t('liveSessions.allLevels')}</option>
                            {LEVEL_ORDER.map((key) => (
                                <option key={key} value={key}>
                                    {t(LEVELS[key].labelKey)}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                            🔎 {t('languageLiveSessions.searchLabel')}
                        </span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('languageLiveSessions.searchPlaceholder')}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500"
                        />
                    </label>
                </div>

                {/* Recordings grid */}
                {hasSessions ? (
                    filteredSessions.length > 0 ? (
                        <div className="space-y-6">
                            <div className="text-sm text-gray-600 dark:text-neutral-400">
                                {t('languageLiveSessions.showing', {
                                    shown: pagedSessions.length,
                                    total: filteredSessions.length,
                                })}
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {pagedSessions.map((session) => {
                                    const level = LEVELS[session.level] ?? LEVELS.general;
                                    return (
                                        <button
                                            key={session.id}
                                            type="button"
                                            onClick={() => setActiveSession(session)}
                                            className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800"
                                        >
                                            <div
                                                className="flex items-center justify-between px-3 py-2 font-bold text-white"
                                                style={{ backgroundColor: level.color }}
                                            >
                                                <span className="line-clamp-2">{session.title}</span>
                                                <Play className="ml-2 h-5 w-5 shrink-0 opacity-80 transition-transform group-hover:scale-110" />
                                            </div>
                                            <div className="space-y-2 p-3">
                                                <span
                                                    className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                                                    style={{ backgroundColor: level.color }}
                                                >
                                                    {t(level.labelKey)}
                                                </span>
                                                {session.professor?.name ? (
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-neutral-300">
                                                        <User className="h-4 w-4" />
                                                        {session.professor.name}
                                                    </div>
                                                ) : null}
                                                {session.duration_minutes > 0 ? (
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-neutral-400">
                                                        <Clock className="h-4 w-4" />
                                                        {t('languageLiveSessions.duration', {
                                                            minutes: session.duration_minutes,
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {totalPages > 1 ? (
                                <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-neutral-700">
                                    <p className="text-sm text-gray-700 dark:text-neutral-300">
                                        {t('languageLiveSessions.page')}{' '}
                                        <span className="font-medium">{page}</span>{' '}
                                        {t('languageLiveSessions.of')}{' '}
                                        <span className="font-medium">{totalPages}</span>
                                    </p>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page <= 1}
                                            className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
                                        >
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                            <span className="ml-1 hidden sm:inline">
                                                {t('languageLiveSessions.previous')}
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={page >= totalPages}
                                            className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
                                        >
                                            <span className="mr-1 hidden sm:inline">
                                                {t('languageLiveSessions.next')}
                                            </span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </nav>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                            <p className="py-8 text-center text-gray-600 dark:text-neutral-400">
                                {t('languageLiveSessions.noResults')}
                            </p>
                        </div>
                    )
                ) : (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                        <div className="py-12 text-center">
                            <Video className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-neutral-500" />
                            <p className="text-lg text-gray-600 dark:text-neutral-400">
                                {t('languageLiveSessions.empty')}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Video modal */}
            {activeSession ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
                    onClick={() => setActiveSession(null)}
                >
                    <div
                        className="w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-neutral-900"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3 p-4">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {activeSession.title}
                            </h3>
                            <button
                                onClick={() => setActiveSession(null)}
                                className="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-neutral-200"
                                aria-label={t('languageLiveSessions.close')}
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <VideoPlayer
                            videoUrl={activeSession.video_url}
                            vimeoUrl={activeSession.vimeo_url}
                        />
                    </div>
                </div>
            ) : null}
        </AppLayout>
    );
};

const LanguageLiveSessions: React.FC<Props> = ({ language_live_sessions }) => (
    <ThemeWrapper>
        <LanguageLiveSessionsContent language_live_sessions={language_live_sessions} />
    </ThemeWrapper>
);

export default LanguageLiveSessions;
